/**
 * race-to-N（先取数）対戦の状態管理（自前・GPL/物理/DOM 非依存・依存ゼロ）。
 *
 * 1ラック=1試合だった既存の対戦に「先取N（race-to-N）」概念を追加する。
 * ブレイク権ルールは「交互（alternate）」/「勝者ブレイク（winner）」を選択可能。
 *
 * 永続化なし: 中断（退出・切断）すると race は破棄される（ロビーへ戻る）。
 * RuleState は引き続き「1ラック単位」で動作し、ラック完了時に本モジュールが集計する。
 *
 * 設計判断:
 *   - 純粋関数のみ（reducer パターン）。controls/main からは状態を保持して関数呼び出し。
 *   - PlayerId は 0|1（既存 nineball rules と一致）。
 *   - vs CPU 対戦と人間対戦の両方で共用する（CPU 定義は `cpu-players.ts`）。
 */

export type RaceTargetWins = 1 | 3 | 5 | 9;
export type BreakRule = 'alternate' | 'winner';

/** race（先取）対戦の進行状態。1ラックごとに rackEnded で更新する。 */
export interface RaceState {
  /** 先取数（目標勝利数）。 */
  readonly targetWins: RaceTargetWins;
  /** ブレイク権ルール。 */
  readonly breakRule: BreakRule;
  /** P0 の勝利ラック数。 */
  readonly winsP0: number;
  /** P1 の勝利ラック数。 */
  readonly winsP1: number;
  /** 現在のラック番号（1始まり）。 */
  readonly rackIndex: number;
  /** 次（または現在）のラックのブレイク権者。 */
  readonly breaker: 0 | 1;
  /** race 終了か。 */
  readonly finished: boolean;
  /** race 終了時の勝者（finished=true のみ意味あり）。 */
  readonly winner: 0 | 1 | null;
  /** 対 CPU 戦か（成績・分析の集計対象外フラグ）。 */
  readonly vsCpu: boolean;
  /** 相手の表示名（CPU の場合は固定文字列）。 */
  readonly opponentName: string;
}

/** race を新規開始する（モーダル選択値から）。 */
export const initRaceState = (params: {
  readonly targetWins: RaceTargetWins;
  readonly breakRule: BreakRule;
  /** 第1ラックのブレイク権者。人間対戦は host=P0 既定、CPU 戦はユーザー=P0（host）既定。 */
  readonly firstBreaker: 0 | 1;
  readonly vsCpu: boolean;
  readonly opponentName: string;
}): RaceState => ({
  targetWins: params.targetWins,
  breakRule: params.breakRule,
  winsP0: 0,
  winsP1: 0,
  rackIndex: 1,
  breaker: params.firstBreaker,
  finished: false,
  winner: null,
  vsCpu: params.vsCpu,
  opponentName: params.opponentName,
});

/** ラック確定（rackWinner）を反映し、次ラックの状態へ進める。
 *  finished=true になった場合は呼び出し側で race 終了処理を行う。 */
export const rackEnded = (state: RaceState, rackWinner: 0 | 1): RaceState => {
  if (state.finished) return state; // 終了後の重複反映を無視
  const winsP0 = state.winsP0 + (rackWinner === 0 ? 1 : 0);
  const winsP1 = state.winsP1 + (rackWinner === 1 ? 1 : 0);
  const reached0 = winsP0 >= state.targetWins;
  const reached1 = winsP1 >= state.targetWins;
  const finished = reached0 || reached1;
  const winner: 0 | 1 | null = finished ? (reached0 ? 0 : 1) : null;
  const nextBreaker: 0 | 1 =
    state.breakRule === 'winner'
      ? rackWinner
      : state.breaker === 0
        ? 1
        : 0;
  return {
    ...state,
    winsP0,
    winsP1,
    rackIndex: state.rackIndex + 1,
    breaker: nextBreaker,
    finished,
    winner,
  };
};

/** スコアボード文字列（"3 - 2 / 先取5" 形式）。 */
export const formatRaceScore = (state: RaceState, myPlayer: 0 | 1): string => {
  const myWins = myPlayer === 0 ? state.winsP0 : state.winsP1;
  const oppWins = myPlayer === 0 ? state.winsP1 : state.winsP0;
  return `${myWins} - ${oppWins} / 先取${state.targetWins}`;
};
